<script lang="ts">
	import WorkingProcessStep from "@components/WorkingProcessStep.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import { onMount } from "svelte";
	import type { ClassValue } from "svelte/elements";
	import { getWorkingProcessAnchor } from "@core/schema/paths/getWorkingProcessAnchor.js";
	import { tw } from "@core/utils/svelte/tw.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";

	const { class: customClass }: { class?: ClassValue } = $props();

	const levelElements = $state<HTMLElement[]>([]);
	let targetElement = $state<HTMLElement>();

	let step = $state(0);
	let lineLength = $state(0);

	let isTargetGlowing = $state(false);

	function recalculateLevel(): void {
		step = levelElements.slice(1).findLastIndex((box) => box.getBoundingClientRect().top < window.innerHeight / 2) + 1;
		const nextStep = levelElements[step + 1];
		const nextStepProgress = nextStep ? window.innerHeight / 2 - nextStep.getBoundingClientRect().top : 0;
		lineLength = Math.max(
			0,
			levelElements.slice(0, Math.min(step + 1)).reduce((prev, curr) => prev + curr.clientHeight, 0) + nextStepProgress,
		);

		const glowingOffset = window.innerHeight / 5;
		isTargetGlowing =
			step + 1 === levelElements.length && targetElement
				? targetElement.getBoundingClientRect().bottom < window.innerHeight - glowingOffset
				: false;
	}

	onMount(() => {
		if (typeof window !== "undefined") {
			recalculateLevel();
		}
	});

	interface ProcessCard {
		headline: string;
		subtitle: string;
		description: string;
		customClass?: ClassValue;
	}

	const analysisLevels: ProcessCard[] = [
		{
			headline: "Level 1",
			subtitle: "Essential",
			description: "1 den v provozu, rychlé zmapování hlavních problémů a příležitostí.",
			customClass: tw("bg-[#7487A4]"),
		},
		{
			headline: "Level 2",
			subtitle: "Professional",
			description: "2 dny sledování a analýzy procesů, prostoru, dat a lidí, včetně priorit a návrhu dalších kroků.",
			customClass: tw("bg-cmcnavy-80"),
		},
		{
			headline: "Level 3",
			subtitle: "Platinum",
			description:
				"3 měsíce kombinace analýzy, interim práce a školení – propojení dat, prostoru a lidí do fungujícího systému.",
			customClass: tw("bg-cmcsecondary"),
		},
	];

	const implementationAndDevelopment: ProcessCard[] = [
		{
			headline: "Commarec Interim",
			subtitle: "Řízení provozu",
			description: "Pomůžeme řídit provoz, nastavíme procesy a stabilizujeme tým.",
			customClass: tw("bg-[#7487A4]"),
		},
		{
			headline: "Commarec Team",
			subtitle: "Projektové řízení",
			description: "Vyřešíme konkrétní úkoly (např. layout skladu, obsah, WMS nebo automatizaci).",
			customClass: tw("bg-cmcnavy-80"),
		},
		{
			headline: "Commarec Academy",
			subtitle: "Školení",
			description: "Naučíme váš tým chápat procesy, data a odpovědnost.",
			customClass: tw("bg-cmcsecondary"),
		},
	];

	const { tenant } = getGlobalTemplateProps();
</script>

<svelte:window onscroll={recalculateLevel} onresize={recalculateLevel} />

<section class={[customClass, "full-width grid-container"]}>
	<h2
		class="text-cmcsecondary text-[1.25rem]/[1.2] md:text-xl/[1.2] lg:text-[2rem]/[1.2] mb-16 text-center font-bold mx-auto max-w-[25.75rem]"
	>
		<Formatted text="Jak probíhá typická spolupráce s Commarec" />
	</h2>

	<div class="full-width relative" id={getWorkingProcessAnchor(tenant)}>
		<WorkingProcessStep level={0} heading="Úvodní seznámení a zadání" bind:root={levelElements[0]} active>
			<p class="font-medium mt-8 md:mt-[5.1875rem] text-2xs/[1.5] md:text-base/[1.5]">
				<Formatted
					text="
						První kontakt slouží k pochopení vašich potřeb. Setkáme se osobně, po telefonu nebo přímo u vás v provozu.
						Zjistíme, co vás trápí, jaké máte cíle a co od spolupráce očekáváte. Společně si definujeme zadání a vyberete si typ úvodní analýzy.
					"
				/>
			</p>
		</WorkingProcessStep>

		<WorkingProcessStep level={1} heading="Úvodní analýza" bind:root={levelElements[1]} active={step >= 1}>
			<p class="mt-2 md:mt-4 mb-8 md:mb-16 font-medium text-2xs/[1.5] md:text-base/[1.5]">
				<Formatted
					text="
						V této fázi mapujeme provoz, měříme ho a připravujeme konkrétní plán. Podle rozsahu provedeme jednu
						z těchto 3 variant analýzy:
					"
				/>
			</p>
			<ol class="flex flex-col gap-1 md:gap-2">
				{#each analysisLevels as { headline, subtitle, description, customClass } (headline)}
					<li
						class={[
							"grid grid-cols-2 gap-4 p-2 md:p-4 rounded-[0.25rem] md:rounded-lg min-h-20 md:min-h-[9.5rem]",
							customClass,
						]}
					>
						<div lang="en" class="text-white">
							<div class="font-druk-cond text-2xl/[0.9] md:text-[3rem]/[0.9] uppercase tracking-[0.02em] font-[1000]">
								<Formatted text={headline} />
							</div>
							<div class="font-medium text-2xs/[1.4] md:text-base/[1.5] uppercase">
								<Formatted text={subtitle} />
							</div>
						</div>
						<p class="font-medium text-2xs/[1.4] md:text-base/[1.5] text-white">
							<Formatted text={description} />
						</p>
					</li>
				{/each}
			</ol>
		</WorkingProcessStep>

		<WorkingProcessStep level={2} heading="Realizace a rozvoj" active={step >= 2} bind:root={levelElements[2]}>
			<p class="mt-2 md:mt-4 mb-8 md:mb-16 font-medium text-2xs/[1.5] md:text-base/[1.5]">
				<Formatted text="Na základě analýzy se spolupráce ve druhé fázi posune do jedné ze tří forem:" />
			</p>
			<ol class="flex flex-col gap-1 md:gap-2">
				{#each implementationAndDevelopment as { headline, subtitle, description, customClass } (headline)}
					<li
						class={[
							"grid grid-cols-2 gap-4 p-2 md:p-4 rounded-[0.25rem] md:rounded-lg min-h-20 md:min-h-[9.5rem]",
							customClass,
						]}
					>
						<div lang="en" class="text-white">
							<div class="font-druk-cond text-2xl/[0.9] md:text-[3rem]/[0.9] uppercase tracking-[0.02em] font-[1000]">
								<Formatted text={headline} />
							</div>
							<div class="font-medium text-2xs/[1.4] md:text-base/[1.5] uppercase">
								<Formatted text={subtitle} />
							</div>
						</div>
						<p class="font-medium text-2xs/[1.4] md:text-base/[1.5] text-white">
							<Formatted text={description} />
						</p>
					</li>
				{/each}
			</ol>
		</WorkingProcessStep>

		<WorkingProcessStep level={3} heading="Vyhodnocení a výsledky" active={step >= 3} bind:root={levelElements[3]}>
			<p class="font-medium mt-8 md:mt-[5.1875rem] text-2xs/[1.5] md:text-base/[1.5]">
				<Formatted
					text="
						Viditelné a měřitelné výsledky jsou pro nás nejvyšší priorita. Po každém projektu je prezentujeme spolu s výstupy
						a doporučeními pro další kroky. Sdílíme konkrétní data i dopady na výkon, náklady a stabilitu týmu. Společně s
						vámi vyhodnotíme, co se podařilo i co je potřeba zlepšit, a určíme další kroky.
					"
				/>
			</p>
		</WorkingProcessStep>

		<WorkingProcessStep level={4} heading="Dlouhodobé partnerství" active={step >= 4} bind:root={levelElements[4]}>
			<p class="font-medium mt-8 md:mt-[5.1875rem] text-2xs/[1.5] md:text-base/[1.5]">
				<Formatted
					text="
						Rádi s vámi budeme spolupracovat i po dokončení projektu. Pomůžeme vám s dalšími fázemi rozvoje, expanzí,
						implementací WMS i následnými audity. Naším cílem je, aby firma rostla a logistika se stala její silnou stránkou,
						ne překážkou.
					"
				/>
			</p>
		</WorkingProcessStep>

		<div class="w-0.5 bg-cmc inset-y-0 left-1/2 -translate-x-1/2 absolute bg-[#DFDFDF]"></div>
		<div class={["w-1 top-0 left-1/2 -translate-x-1/2 absolute h-full translate"]}>
			<div class="relative size-full">
				<div
					class={[
						"bg-cmcprimary sticky top-0",
						isTargetGlowing ? "h-full" : "h-[50vh]",
						step > 1 && "transition-all duration-[600ms]",
					]}
					style={`max-height: ${lineLength}px`}
				></div>
			</div>
		</div>
		<div class="size-3 md:size-6 bg-cmcprimary rounded-full left-1/2 top-0 -translate-x-1/2 absolute"></div>
	</div>

	<div
		bind:this={targetElement}
		class={[
			"mx-auto border-current border-4 rounded-xl p-8 font-extrabold uppercase text-xl/[.7] tracking-[0.005] transition-all delay-[100ms] duration-[300ms]",
			isTargetGlowing ? "text-cmcprimary" : "text-[#DFDFDF]",
		]}
	>
		<Formatted text="Cíl" />
	</div>
</section>
